window.ui = window.ui || {};

/**
 * 가격 포맷팅
 * Shopify money format 적용
 */
function formatMoney(cents) {
  // Shopify 설정의 money format 사용
  // 기본값: $15.00 형식
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

class CartManager {
  constructor() {
    this.cartItems = [];
    this.init();
  }

  async init() {
    await this.loadCart();
    this.bindEvents();
  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.cartItems = cart.items;
    } catch (error) {
      console.error('Failed to load cart:', error);
      this.cartItems = [];
    }
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-event]');
      const method = button?.dataset?.cartEvent;
      if (button && method) {
        event.preventDefault();
        const loadingTarget = button.closest('[data-item-key]');
        const productKey = loadingTarget.dataset.itemKey;
        if (method === 'add') {
          this.handleAddToCart(productKey, loadingTarget);
        } else if (method === 'remove') {
          this.handleRemoveFromCart(productKey, loadingTarget);
        }
      }
    });
  }

  async handleAddToCart(key, button) {
    if (!key) {
      window.ui.alert.error('Product information not found.');
      return;
    }
    this.setButtonLoading(button, true);

    try {
      // 현재 장바구니 상태 새로고침
      await this.loadCart();

      // 장바구니에 이미 있는지 확인
      const existingItem = this.cartItems.find((item) => item.variant_id.toString() === key.toString());
      if (existingItem) {
        window.ui.alert.warning('This item is already in your cart.');
        this.setButtonLoading(button, false);
        console.log(existingItem);
        return;
      }

      // 장바구니에 추가
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 1
        })
      });

      if (addResponse.ok) {
        // 장바구니 업데이트
        await this.loadCart();
        window.ui.alert.success('Added to cart successfully!');

        // 장바구니 개수 업데이트
        this.updateCartUi();
      } else {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add item to cart.');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      window.ui.alert.error(error.message || 'An error occurred while adding to cart.');
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  async handleRemoveFromCart(key, button) {
    if (!key) {
      window.ui.alert.error('Product information not found.');
      return;
    }
    this.setButtonLoading(button, true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      await this.updateCartUi();
      this.setButtonLoading(button, false);
    } catch (error) {
      console.error('Remove cart item failed:', error);
      window.ui.alert.error('Failed to remove item. Please try again.');
      this.setButtonLoading(button, false);
    }
  }

  setButtonLoading(button, isLoading) {
    if (!button) return;
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
    } else {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  async updateCartUi() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartContentBody = document.getElementById('cart-content');
      const cartContentFooter = document.getElementById('cart-footer');

      const itemCount = cart.items.length;
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = itemCount;
      });
      document.querySelectorAll('[data-cart-count-text]').forEach((el) => {
        el.textContent = itemCount === 1 ? 'item' : 'items';
      });
      document.querySelectorAll('[data-cart-total-price]').forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });

      if (cartContentBody) {
        if (itemCount > 0) {
          cartContentBody.innerHTML = `<div class="cart-items-list" id="cart-items-list">
            ${cart.items
              .map(
                (item, index) => `
              <div class="cart-item" data-line-index="${index + 1}" data-item-key="${item.key}">
                <div class="cart-item__image">
                  <a href="${item.url}">
                    <img src="${item.featured_image.url}&amp;width=${item.featured_image.width}" alt="${item.featured_image.alt}" width="${item.featured_image.width}" height="${item.featured_image.height}" >
                  </a>
                </div>
                <div class="cart-item__details">
                  <p class="cart-item__title">${item.product_title}</p>
                  <p class="cart-item__price">${formatMoney(item.final_price)}</p>
                </div>
                <button
                    type="button"
                    class="cart-item__remove"
                    data-cart-event="remove"
                    aria-label="Remove item"
                  >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 22L22 10" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M22 22L10 10" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                </button>
              </div>
            `
              )
              .join('')}
          </div>`;
          cartContentFooter?.removeAttribute('hidden');
        } else {
          cartContentBody.innerHTML = '<div class="empty-cart-message">Your cart is currently empty.</div>';
          cartContentFooter?.setAttribute('hidden', 'true');
        }
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart, itemCount }
        })
      );

      return cart;
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }
}

// DOM 로드 완료 후 초기화
document.addEventListener('DOMContentLoaded', () => {
  window.ui.cart = new CartManager();
});